

export interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresIn: number; // in milliseconds
}

class CacheManager {
  private prefix = 'shetkari_cache_';

  
  set<T>(key: string, data: T, expiresIn: number = 5 * 60 * 1000): void {
    try {
      const cacheItem: CacheItem<T> = {
        data,
        timestamp: Date.now(),
        expiresIn
      };
      localStorage.setItem(this.prefix + key, JSON.stringify(cacheItem));
    } catch (error) {
      console.error('Error setting cache:', error);
    }
  }

  
  get<T>(key: string): T | null {
    try {
      const cached = localStorage.getItem(this.prefix + key);
      if (!cached) {
        return null;
      }

      const cacheItem: CacheItem<T> = JSON.parse(cached);
      const now = Date.now();
      const age = now - cacheItem.timestamp;

      if (age > cacheItem.expiresIn) {
        this.delete(key);
        return null;
      }

      return cacheItem.data;
    } catch (error) {
      console.error('Error getting cache:', error);
      return null;
    }
  }

  
  delete(key: string): void {
    try {
      localStorage.removeItem(this.prefix + key);
    } catch (error) {
      console.error('Error deleting cache:', error);
    }
  }

  
  clearAll(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  
  clearExpired(): void {
    try {
      const keys = Object.keys(localStorage);
      let cleared = 0;
      
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          const cached = localStorage.getItem(key);
          if (cached) {
            try {
              const cacheItem: CacheItem<any> = JSON.parse(cached);
              const age = Date.now() - cacheItem.timestamp;
              if (age > cacheItem.expiresIn) {
                localStorage.removeItem(key);
                cleared++;
              }
            } catch (e) {
              localStorage.removeItem(key);
              cleared++;
            }
          }
        }
      });
    } catch (error) {
      console.error('Error clearing expired cache:', error);
    }
  }

  
  has(key: string): boolean {
    return this.get(key) !== null;
  }
}

export const cache = new CacheManager();

cache.clearExpired();
